"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import {
  createRedirectAction,
  updateRedirectAction,
  deleteRedirectAction,
  toggleRedirectAction,
} from "./_actions";

export type Redirect = {
  id: string;
  fromPath: string;
  toPath: string;
  statusCode: number;
  hits: number;
  lastHitAt: Date | null;
  enabled: boolean;
};

export function RedirectCreateForm() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await createRedirectAction(fd);
      if (!res.ok) setError(res.error);
      else {
        (e.target as HTMLFormElement).reset();
        setOpen(false);
        router.refresh();
      }
    });
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-4 py-2 rounded-lg bg-accent-primary text-white text-sm font-medium hover:bg-accent-primary/90 transition mb-6"
      >
        + Thêm redirect
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-stroke-subtle bg-bg-elevated/40 p-5 space-y-3 mb-6"
    >
      <h2 className="font-display font-semibold">Redirect mới</h2>
      {error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs">
          From path *
          <input
            name="fromPath"
            required
            placeholder="/old-slug"
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm font-mono"
          />
        </label>
        <label className="text-xs">
          To path / URL *
          <input
            name="toPath"
            required
            placeholder="/new-slug hoặc https://..."
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm font-mono"
          />
        </label>
        <label className="text-xs">
          Status code
          <select
            name="statusCode"
            defaultValue={301}
            className="mt-1 w-full px-3 py-2 rounded-lg bg-bg-elevated border border-stroke-subtle text-sm"
          >
            <option value={301}>301 Permanent</option>
            <option value={302}>302 Temporary</option>
            <option value={307}>307 Temporary (preserve method)</option>
            <option value={308}>308 Permanent (preserve method)</option>
          </select>
        </label>
        <label className="text-xs flex items-center gap-2 mt-5">
          <input type="checkbox" name="enabled" defaultChecked />
          <span>Bật ngay</span>
        </label>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-4 py-1.5 rounded text-sm hover:bg-bg-overlay"
        >
          Hủy
        </button>
        <button
          type="submit"
          disabled={pending}
          className="px-4 py-1.5 rounded bg-accent-primary text-white text-sm font-medium disabled:opacity-50"
        >
          {pending ? "Đang tạo…" : "Tạo redirect"}
        </button>
      </div>
    </form>
  );
}

export function RedirectRow({ r }: { r: Redirect }) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);

  function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    startTransition(async () => {
      const res = await updateRedirectAction(r.id, fd);
      if (!res.ok) setError(res.error);
      else {
        setEditing(false);
        router.refresh();
      }
    });
  }

  function toggle() {
    startTransition(async () => {
      await toggleRedirectAction(r.id, !r.enabled);
      router.refresh();
    });
  }

  function remove() {
    if (!confirm(`Xóa redirect "${r.fromPath}" → "${r.toPath}"?`)) return;
    startTransition(async () => {
      const res = await deleteRedirectAction(r.id);
      if (!res.ok) setError(res.error);
      else router.refresh();
    });
  }

  if (editing) {
    return (
      <tr className="border-b border-stroke-subtle/60">
        <td colSpan={6} className="p-4 bg-bg-elevated/30">
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-3">
            {error && <div className="sm:col-span-2 text-sm text-rose-300">{error}</div>}
            <label className="text-xs">
              From
              <input
                name="fromPath"
                defaultValue={r.fromPath}
                required
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm font-mono"
              />
            </label>
            <label className="text-xs">
              To
              <input
                name="toPath"
                defaultValue={r.toPath}
                required
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm font-mono"
              />
            </label>
            <label className="text-xs">
              Status
              <select
                name="statusCode"
                defaultValue={r.statusCode}
                className="mt-1 w-full px-2.5 py-1.5 rounded bg-bg-elevated border border-stroke-subtle text-sm"
              >
                <option value={301}>301</option>
                <option value={302}>302</option>
                <option value={307}>307</option>
                <option value={308}>308</option>
              </select>
            </label>
            <label className="text-xs flex items-center gap-2 mt-5">
              <input type="checkbox" name="enabled" defaultChecked={r.enabled} />
              <span>Bật</span>
            </label>
            <div className="sm:col-span-2 flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError(null);
                }}
                className="px-3 py-1.5 rounded text-sm hover:bg-bg-overlay"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={pending}
                className="px-4 py-1.5 rounded bg-accent-primary text-white text-sm font-medium disabled:opacity-50"
              >
                {pending ? "Đang lưu…" : "Lưu"}
              </button>
            </div>
          </form>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-stroke-subtle/60 last:border-0 hover:bg-bg-elevated/30 transition">
      <td className="px-4 py-3">
        <code className="text-xs font-mono">{r.fromPath}</code>
      </td>
      <td className="px-4 py-3">
        <code className="text-xs font-mono text-accent-primary">{r.toPath}</code>
      </td>
      <td className="px-4 py-3 text-sm text-ink-secondary">{r.statusCode}</td>
      <td className="px-4 py-3 text-sm text-ink-muted">
        {r.hits.toLocaleString("vi-VN")}
        {r.lastHitAt && (
          <div className="text-[10px] text-ink-muted/70 mt-0.5">
            {new Intl.DateTimeFormat("vi-VN", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }).format(r.lastHitAt)}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={toggle}
          disabled={pending}
          className={`text-xs px-2 py-0.5 rounded-full ${
            r.enabled
              ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
              : "bg-zinc-500/15 text-zinc-400 hover:bg-zinc-500/25"
          } transition`}
        >
          {r.enabled ? "Bật" : "Tắt"}
        </button>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {error && <div className="text-xs text-rose-300 mb-1">{error}</div>}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-accent-primary hover:underline mr-3"
        >
          Sửa
        </button>
        <button
          type="button"
          onClick={remove}
          disabled={pending}
          className="text-xs text-rose-300 hover:underline disabled:opacity-50"
        >
          Xóa
        </button>
      </td>
    </tr>
  );
}
